"use client";

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Download, Zap, Shield, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeBackground, AuroraBlobs } from "@/components/ui/theme-utils";
import {
  PrimaryButton,
  SecondaryButton,
  FloatingThemeToggle
} from "@/components/ui/buttons";
import { Navbar } from "@/components/ui/navbar";
import { Footer } from "@/components/ui/footer";
import { Features } from "@/components/ui/features";
import { HowItWorks } from "@/components/ui/how-it-works";
import { UseCases } from "@/components/ui/use-cases";
import { Security } from "@/components/ui/security";
import { Testimonials } from "@/components/ui/testimonials";
import { FAQ } from "@/components/ui/faq";
import { Stats } from "@/components/ui/stats";
import { CTABanner } from "@/components/ui/cta-banner";
import { LogoMarquee } from "@/components/ui/marquee";
import { HeroMockup } from "@/components/ui/hero-mockup";

export default function HomePage() {
  const { isDark } = useTheme();

  return (
    <ThemeBackground className="min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-x-clip overflow-y-visible pt-36 sm:pt-44 pb-16">
        <div
          className={cn(
            "absolute inset-0 -z-10",
            isDark ? "bg-mesh-dark" : "bg-mesh-light"
          )}
        />
        <div
          className={cn(
            "absolute inset-0 -z-10 opacity-40",
            isDark ? "bg-grid" : "bg-grid"
          )}
        />
        <AuroraBlobs variant="mixed" />

        <div className="container mx-auto px-5 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
            <div className="lg:col-span-7 text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className={cn(
                  "inline-flex items-center gap-2 rounded-full pl-1.5 pr-4 py-1.5 mb-7",
                  isDark
                    ? "bg-white/[0.04] border border-white/10"
                    : "bg-white/80 border border-stone-200 shadow-sm"
                )}
              >
                <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-brand-700 to-brand-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                  <Sparkles className="h-3 w-3" />
                  v3
                </span>
                <span
                  className={cn(
                    "text-sm font-medium",
                    isDark ? "text-white/80" : "text-stone-700"
                  )}
                >
                  Auto-retry, mid-file resume, zero monitoring
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05 }}
                className="responsive-heading-1"
              >
                Move terabytes{" "}
                <span className="relative inline-block">
                  <span className="text-gradient">at line rate</span>
                  <svg
                    viewBox="0 0 300 12"
                    className="absolute -bottom-2 left-0 w-full h-2"
                    preserveAspectRatio="none"
                    aria-hidden
                  >
                    <path
                      d="M2 8 Q 75 0, 150 6 T 298 4"
                      fill="none"
                      stroke="url(#g)"
                      strokeWidth="3"
                      strokeLinecap="round"
                    />
                    <defs>
                      <linearGradient id="g" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#52525B" />
                        <stop offset="50%" stopColor="#09090B" />
                        <stop offset="100%" stopColor="#27272A" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
                .
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.12 }}
                className={cn(
                  "mt-7 text-lg sm:text-xl max-w-2xl mx-auto lg:mx-0 leading-relaxed",
                  isDark ? "text-white/70" : "text-stone-600"
                )}
              >
                The file-transfer layer for production teams shipping hundreds
                of GB every day. Secure, resumable, fast as your link allows.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.18 }}
                className="mt-9 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3"
              >
                <PrimaryButton
                  href="/downloads"
                  size="lg"
                  icon={<Download className="h-4 w-4" />}
                >
                  Download the app
                </PrimaryButton>
                <SecondaryButton
                  href="https://cal.com/zset.in/demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="lg"
                  icon={<ArrowRight className="h-4 w-4" />}
                >
                  Book a 20-min demo
                </SecondaryButton>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className={cn(
                  "mt-8 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-xs",
                  isDark ? "text-white/50" : "text-stone-500"
                )}
              >
                <span className="inline-flex items-center gap-1.5">
                  <Shield className="h-3.5 w-3.5" /> Encrypted packets
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5" /> Up to 1 Gbps
                </span>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="lg:col-span-5"
            >
              <HeroMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="py-10 sm:py-14 border-y border-stone-200/60 dark:border-white/5">
        <div className="container mx-auto px-5 sm:px-6">
          <p
            className={cn(
              "text-center text-xs uppercase tracking-[0.2em] font-medium mb-6",
              isDark ? "text-white/40" : "text-stone-500"
            )}
          >
            Trusted by production studios, agencies & enterprises
          </p>
        </div>
        <LogoMarquee />
      </section>

      {/* STATS */}
      <Stats />

      {/* FEATURES BENTO */}
      <Features />

      {/* HOW IT WORKS */}
      <HowItWorks />

      {/* USE CASES */}
      <UseCases />

      {/* SECURITY */}
      <Security />

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* FAQ */}
      <FAQ />

      {/* FINAL CTA */}
      <CTABanner />

      {/* FOOTER */}
      <Footer />

      <FloatingThemeToggle />
    </ThemeBackground>
  );
}
